import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { config } from '../../../config';
import { useAuth } from '../../../context/AuthContext';
import SiteNavBar from '../navigation/SiteNavBar';
import Footer from '../footer/Footer';
import CopyRight from '../footer/CopyRight';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderNumber, orderId } = location.state || {};
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch order details from backend
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${config.API_BASE}/api/orders/${orderId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, token]);

  // Auto-refresh order status when PENDING (waiting for webhook to update status)
  useEffect(() => {
    if (!orderId || !order || order.status !== 'PENDING') {
      return; // Only poll if order is PENDING
    }

    console.log('Order status is PENDING, starting auto-refresh polling...');

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${config.API_BASE}/api/orders/${orderId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        console.log('Polling order status:', response.data.status);

        if (response.data.status !== 'PENDING') {
          // Status changed! Update order and stop polling
          console.log('Payment status updated to:', response.data.status);
          setOrder(response.data);
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Error polling order status:', error);
      }
    }, 3000); // Poll every 3 seconds

    // Cleanup: stop polling after 2 minutes max (safety timeout)
    const timeout = setTimeout(() => {
      console.log('Stopping auto-refresh after 2 minutes');
      clearInterval(pollInterval);
    }, 120000);

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [orderId, order, token]);

  return (
    <div>
      <SiteNavBar />
      <div className="hero-area hero-bg" style={{ paddingTop: '3%' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-9 offset-lg-2 text-center">
              <div className="hero-text">
                <div className="hero-text-tablecell" style={{ padding: '15% 0' }}>
                  <p className="subtitle" style={{ fontSize: '20px' }}>Farmers Marketspace</p>
                  <h1>Order Confirmed!</h1>

                  {orderNumber && (
                    <div style={{
                      margin: '20px 0',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      display: 'inline-block'
                    }}>
                      <p style={{ margin: 0, fontSize: '16px', color: '#666' }}>Order Number</p>
                      <h2 style={{ margin: '5px 0', color: '#28a745', fontSize: '32px', fontWeight: 'bold' }}>
                        #{orderNumber}
                      </h2>
                    </div>
                  )}

                  {loading ? (
                    <div style={{
                      marginTop: '20px',
                      padding: '20px',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '8px',
                      maxWidth: '600px',
                      margin: '20px auto'
                    }}>
                      <p style={{ margin: 0, color: '#666' }}>Loading order details...</p>
                    </div>
                  ) : (
                    <>
                      {order?.status === 'CONFIRMED' && (
                        <div style={{
                          marginTop: '20px',
                          padding: '25px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '8px',
                          maxWidth: '600px',
                          margin: '20px auto',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          <p style={{ fontSize: '22px', color: '#28a745', fontWeight: 'bold', margin: '0 0 15px 0' }}>
                            ✓ Payment Successful!
                          </p>
                          <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>
                            Thank you for your order. Your order has been placed successfully.<br/>
                            You will receive a confirmation email with your order details soon.
                          </p>
                        </div>
                      )}
                      {order?.status === 'PENDING' && (
                        <div style={{
                          marginTop: '20px',
                          padding: '25px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '8px',
                          maxWidth: '600px',
                          margin: '20px auto',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          border: '2px solid #ffc107'
                        }}>
                          <p style={{ fontSize: '22px', color: '#ff8c00', fontWeight: 'bold', margin: '0 0 15px 0' }}>
                            ⏳ Payment Processing...
                          </p>
                          <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>
                            We're processing your payment. This usually takes a few moments.<br/>
                            We'll update you once your payment is confirmed.<br/>
                            You can check your order status in "My Orders" below.
                          </p>
                        </div>
                      )}
                      {order?.status === 'CANCELLED' && (
                        <div style={{
                          marginTop: '20px',
                          padding: '25px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '8px',
                          maxWidth: '600px',
                          margin: '20px auto',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          border: '2px solid #dc3545'
                        }}>
                          <p style={{ fontSize: '22px', color: '#dc3545', fontWeight: 'bold', margin: '0 0 15px 0' }}>
                            ✗ Payment Failed
                          </p>
                          <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>
                            Unfortunately, your payment could not be processed.<br/>
                            Please try placing your order again or contact support if the problem persists.
                          </p>
                        </div>
                      )}
                      {!order && orderId && (
                        <div style={{
                          marginTop: '20px',
                          padding: '25px',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '8px',
                          maxWidth: '600px',
                          margin: '20px auto'
                        }}>
                          <p style={{ margin: 0, color: '#333' }}>
                            Thank you for your order. You can check your order status below.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ marginTop: '30px' }}>
                    <button
                      className="boxed-btn"
                      onClick={() => navigate('/account/dashboard?tab=orders')}
                      style={{
                        padding: '15px 40px',
                        fontSize: '16px',
                        cursor: 'pointer'
                      }}
                    >
                      View My Orders
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <CopyRight />
    </div>
  );
};

export default OrderConfirmation; 